import { useEffect } from 'react';
import { useIsAcomba } from './useIsAcomba';
import { useTenantStore } from '../stores/tenantStore';
import { useAcombaBridgeStore } from '../stores/acombaBridgeStore';
import { useRevalidateTransphereStatus } from './useRevalidateTransphereStatus';
import { useTransphereStatusStore } from '../stores/transphereStatusStore';
import { desktopTransphereBannerVariant } from '../utils/transphere/desktopTransphereState';
import type { TransphereBannerVariant as DesktopTransphereBannerVariant } from '../utils/transphere/desktopTransphereState';
import type { EncaissementInProgressStatus } from '../services/transphereService';

export type { EncaissementInProgressStatus };

const IN_PROGRESS_PREFIX = 'encaissement-in-progress-' as const;

const IN_PROGRESS_STATUSES = ['processing', 'pending', 'incomplete', 'rejected'] as const;

// An unrecognized value from GO.Connect must not become a variant key IN_PROGRESS_COPY has no
// entry for — no ErrorBoundary in client/src, so that would blank the whole page on render.
function isEncaissementInProgressStatus(value: unknown): value is EncaissementInProgressStatus {
    return typeof value === 'string' && (IN_PROGRESS_STATUSES as readonly string[]).includes(value);
}

/** Portal/browser-only variants: an encaissement subscription request already under review, one
 * sub-state per step the AcombaGO configuration screen distinguishes. There is no desktop
 * equivalent — the Acomba bridge has no source for this granularity. */
export type EncaissementInProgressVariant = `${typeof IN_PROGRESS_PREFIX}${EncaissementInProgressStatus}`;

export type TransphereBannerVariant = DesktopTransphereBannerVariant | EncaissementInProgressVariant;

/** Narrows a variant down to its in-progress sub-state, or null when it isn't one. */
export function encaissementInProgressStatusOf(variant: TransphereBannerVariant): EncaissementInProgressStatus | null {
    return variant.startsWith(IN_PROGRESS_PREFIX)
        ? (variant.slice(IN_PROGRESS_PREFIX.length) as EncaissementInProgressStatus)
        : null;
}

/**
 * Resolves which Transphere banner (if any) applies to the current tenant.
 *
 * Desktop (Acomba bridge) sessions have no AcceoConnect session to ask, so the variant is derived from
 * acombaBridgeStore instead — see desktopTransphereBannerVariant. Only that path can report
 * `encaissement-subscription-inactive`: the Connect status already folds the receipt subscription into
 * `encaissementActive`, where a lapsed one is indistinguishable from collection never configured.
 *
 * Browser/portal sessions read the shared transphereStatusStore, so multiple consumers on the same page
 * share one backend call. It refetches on a tenant switch and on every way back into the panel — see
 * useRevalidateTransphereStatus.
 */
export function useTransphereStatus(): TransphereBannerVariant | null {
    const isAcomba = useIsAcomba();
    const tenantSwitchedAt = useTenantStore(s => s.tenantSwitchedAt);
    const bridgeSessionStatus = useAcombaBridgeStore(s => s.sessionStatus);
    const bridgeCompanyInfoStatus = useAcombaBridgeStore(s => s.companyInfoStatus);
    const bridgeTransphereActive = useAcombaBridgeStore(s => s.companyTransphereActive);
    const bridgeTransphereUniqueId = useAcombaBridgeStore(s => s.companyTransphereUniqueId);
    const bridgeTransphereEncaissementActif = useAcombaBridgeStore(s => s.companyTransphereEncaissementActif);
    const bridgeReceiptSubscription = useAcombaBridgeStore(s => s.companyTransphereReceiptSubscription);
    const resolved = useTransphereStatusStore(s => s.resolved);
    const status = useTransphereStatusStore(s => s.status);
    const fetchStatus = useTransphereStatusStore(s => s.fetchStatus);

    useEffect(() => {
        if (!isAcomba) void fetchStatus();
    }, [isAcomba, tenantSwitchedAt, fetchStatus]);

    useRevalidateTransphereStatus(isAcomba);

    if (isAcomba) {
        return desktopTransphereBannerVariant({
            connected: bridgeSessionStatus === 'connected',
            companyResolved: bridgeCompanyInfoStatus === 'resolved',
            transphereActive: bridgeTransphereActive,
            transphereUniqueId: bridgeTransphereUniqueId,
            encaissementActif: bridgeTransphereEncaissementActif,
            receiptSubscription: bridgeReceiptSubscription,
        });
    }

    if (!resolved || !status) return null;
    if (!status.subscribed) return 'not-subscribed';
    if (!status.encaissementActive) {
        return isEncaissementInProgressStatus(status.encaissementInProgressStatus)
            ? `${IN_PROGRESS_PREFIX}${status.encaissementInProgressStatus}`
            : 'subscribed-no-encaissement';
    }
    return null;
}
